/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DiagnosticLogger } from "./DiagnosticLogger";
import type { LogDetails } from "../types";
import { DiagnosticSeverity } from "../types";

/**
 * Backend diagnostics sink for the playback module.
 *
 * Exposes the leveled `error`/`info`/`debug`/`warn(tag, message, ...params)`
 * API so call sites can pair a `Diagnostics.<level>(...)` with a matching
 * `Log.<letter>(...)` using identical arguments. Internally it routes into the
 * app's structured {@link DiagnosticLogger}, using the class `tag` as the
 * diagnostic name and carrying the tag plus any extra params in `details`.
 */
function stringifyParam(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (value instanceof Error) {
    return value.stack === undefined ? value.message : value.stack;
  }
  try {
    return JSON.stringify(value);
  } catch (error: unknown) {
    return `${String(value)} <unserializable: ${String(error)}>`;
  }
}

function buildDetails(tag: string, optionalParams: unknown[]): LogDetails {
  const details: LogDetails = { tag };
  if (optionalParams.length > 0) {
    details.params = optionalParams
      .map((param: unknown): string => stringifyParam(param))
      .join(" ");
  }
  return details;
}

export const Diagnostics = {
  error(tag: string, message: string, ...optionalParams: unknown[]): void {
    DiagnosticLogger.log(
      tag,
      message,
      DiagnosticSeverity.Error,
      buildDetails(tag, optionalParams)
    );
  },
  warn(tag: string, message: string, ...optionalParams: unknown[]): void {
    DiagnosticLogger.log(
      tag,
      message,
      DiagnosticSeverity.Warning,
      buildDetails(tag, optionalParams)
    );
  },
  info(tag: string, message: string, ...optionalParams: unknown[]): void {
    DiagnosticLogger.log(
      tag,
      message,
      DiagnosticSeverity.Info,
      buildDetails(tag, optionalParams)
    );
  },
  debug(tag: string, message: string, ...optionalParams: unknown[]): void {
    DiagnosticLogger.log(
      tag,
      message,
      DiagnosticSeverity.Debug,
      buildDetails(tag, optionalParams)
    );
  }
};
